import { BenchmarkMetric, BentoItem, HorizontalChapter, PlaygroundPreset, TestimonialVoice } from '../types/landing';

export const PROSE_REVEAL_STATEMENTS = [
  "Every frame rendered.",
  "Every token streamed.",
  "Every spring physics curve interpolated.",
  "Every interaction resolved without friction.",
  "That is modern web craft.",
  "Ordinary code outputs static markup.",
  "Disciplined engineering creates visceral emotion.",
  "120 FPS fluid motion. Zero stutter. Sub-millisecond responsiveness.",
  "example.th157le.ru was engineered to establish the new benchmark.",
];

export const HORIZONTAL_CHAPTERS: HorizontalChapter[] = [
  {
    id: "chapter-01",
    index: "01",
    tag: "WEBGL 3D FIELD",
    title: "Topological Neural Manifold",
    subtitle: "12,000 real-time particles with GPU spring physics and cursor gravimetry.",
    description: "Built on custom GLSL shaders and Three.js buffers. The topology warps dynamically in response to scroll velocity and viewport proximity without dropping below 120 FPS.",
    metrics: [
      { label: "PARTICLE DENSITY", value: "12,000" },
      { label: "GPU RENDER LATENCY", value: "0.8ms" },
      { label: "SHADERS", value: "CUSTOM GLSL" },
    ],
    accentColor: "#ffaf01",
  },
  {
    id: "chapter-02",
    index: "02",
    tag: "STREAM KINETICS",
    title: "Sub-Millisecond Real-Time Telemetry",
    subtitle: "Instantaneous data streaming simulation with real-time TTFT metrics.",
    description: "Engineered for instantaneous data streaming throughput. Characters stream across the virtual terminal at speeds surpassing human visual perception, with animated millisecond telemetry.",
    metrics: [
      { label: "PEAK VELOCITY", value: "542 T/S" },
      { label: "TTFT", value: "18.4 MS" },
      { label: "BUFFER JITTER", value: "< 0.2%" },
    ],
    accentColor: "#f55036",
  },
  {
    id: "chapter-03",
    index: "03",
    tag: "HARDWARE GEOMETRY",
    title: "Double-Bezel Doppelrand Enclosure",
    subtitle: "Machined aluminum tactile depth with concentric hairline borders.",
    description: "Physical enclosures designed like luxury watchcases: an outer shell with 1px hairline border and an inner core with concentric squircle radii and dynamic reflection блик.",
    metrics: [
      { label: "INNER RADIUS", value: "CALC(R-2PX)" },
      { label: "SPECULAR INSET", value: "1PX HAIRLINE" },
      { label: "TILT AXIS", value: "3D PITCH/ROLL" },
    ],
    accentColor: "#d4af37",
  },
  {
    id: "chapter-04",
    index: "04",
    tag: "FLUID DYNAMICS",
    title: "Lenis Virtualized Scroll Engine",
    subtitle: "Custom cubic-bezier interpolation synchronized with GSAP tickers.",
    description: "Zero native browser stutter. All wheel and trackpad inputs are captured and interpolated through custom physical spring curves, ensuring total momentum preservation.",
    metrics: [
      { label: "INTERPOLATION", value: "EXPONENTIAL" },
      { label: "TICKER DRIFT", value: "0.00 MS" },
      { label: "GPU PAINTING", value: "COMPOSITOR ONLY" },
    ],
    accentColor: "#fa500f",
  },
];

export const BENTO_ARCHITECTURE_ITEMS: BentoItem[] = [
  {
    id: "bento-1",
    colSpan: "lg:col-span-8",
    tag: "CORE SUBSYSTEM",
    title: "Sub-Millisecond Response Fabric",
    description: "Engineered without layout shifts or expensive DOM recalcs. Every interactive gesture is mapped exclusively to GPU-accelerated translate3d and opacity layers.",
    technicalSpec: "NO_REFLOW // COMPOSITOR_THREAD_ONLY",
    metric: { label: "FRAME TIME CONSISTENCY", value: "99.8", unit: "%" },
    badge: "BENCHMARK LEADER",
  },
  {
    id: "bento-2",
    colSpan: "lg:col-span-4",
    tag: "TYPOGRAPHY",
    title: "Swiss Grid Dividers",
    description: "Mathematical border rules and hairline dividers inspired by modernist typographic architecture and Swiss editorial print posters.",
    technicalSpec: "BORDER_RGB // 255,255,255,0.08",
    metric: { label: "GRID STRICTNESS", value: "100", unit: "%" },
  },
  {
    id: "bento-3",
    colSpan: "lg:col-span-4",
    tag: "MICRO-INTERACTIONS",
    title: "Magnetic Button Physics",
    description: "Interactive capsules that pull toward the cursor with simulated gravitational mass, featuring encapsulated 8-bit directional arrows.",
    technicalSpec: "SPRING_STIFFNESS // 180",
    metric: { label: "MAGNETIC RADIUS", value: "48", unit: "PX" },
  },
  {
    id: "bento-4",
    colSpan: "lg:col-span-4",
    tag: "TELEMETRY HUD",
    title: "Embedded Craft Inspector",
    description: "Real-time diagnostic HUD monitoring frame rate, scroll velocity, active GSAP triggers, and WebGL buffer memory.",
    technicalSpec: "RAF_TELEMETRY // LIVE",
    metric: { label: "REFRESH RATE", value: "120", unit: "HZ" },
    badge: "BUILT-IN",
  },
  {
    id: "bento-5",
    colSpan: "lg:col-span-4",
    tag: "COLOR SCIENCE",
    title: "Warm Editorial Espresso",
    description: "Haute-couture palette merging obsidian graphite (#070709), vintage warm cream (#F5F2EB), and stepped sunset gradients.",
    technicalSpec: "OKLCH_CALIBRATED // NO_FATIGUE",
    metric: { label: "CONTRAST RATIO", value: "18.4", unit: ":1" },
  },
];

export const PLAYGROUND_PRESETS: PlaygroundPreset[] = [
  {
    id: "code-gen",
    title: "Zero-Latency Shader Kernel",
    prompt: "Synthesize a WebGL fragment shader for an organic pulsating gravitational anomaly.",
    response: `// Hardware Accelerated GLSL Fragment Shader
precision highp float;
uniform vec2 u_resolution;
uniform float u_time;
uniform vec2 u_mouse;

void main() {
    vec2 st = (gl_FragCoord.xy * 2.0 - u_resolution) / min(u_resolution.x, u_resolution.y);
    float d = length(st) * 2.4;
    vec3 col = vec3(0.02, 0.02, 0.03);
    
    // Kinetic gravity waves
    float ring = sin(d * 14.0 - u_time * 4.0) * 0.5 + 0.5;
    vec3 gold = vec3(0.83, 0.68, 0.22);
    vec3 flame = vec3(0.96, 0.31, 0.21);
    col += mix(gold, flame, sin(u_time * 0.8)) * pow(ring, 4.0) * (1.2 / d);
    
    gl_FragColor = vec4(col, 1.0);
}`,
    tokensPerSec: 548,
    ttftMs: 14.2,
    totalTokens: 184,
  },
  {
    id: "agentic-reasoning",
    title: "Autonomous Agent Reasoning Chain",
    prompt: "Decompose a mission-critical zero-downtime database migration across 4 continental regions.",
    response: `[PHASE 01: TOPOLOGY RECONNAISSANCE]
• Validating replication lag across eu-central-1, us-east-1, ap-northeast-1.
• Mean consensus latency: 18.2ms. Consensus quorum established.

[PHASE 02: SHADOW DUAL-WRITE PIPELINE]
• Spawning Kafka sync consumers on partition 0..128.
• Verifying checksums with zero-overhead streaming CRC-64... MATCH (100.000%).

[PHASE 03: INSTANTANEOUS TRAFFIC SWAP]
• Global DNS BGP anycast route shifting to v2 schema.
• Total downtime: 0.000 milliseconds. 100% queries served without dropped packets.`,
    tokensPerSec: 512,
    ttftMs: 16.8,
    totalTokens: 142,
  },
  {
    id: "system-architecture",
    title: "High-Throughput LPU Memory Architecture",
    prompt: "Describe the architectural distinction between deterministic SRAM scheduling vs DDR5 contention.",
    response: `Unlike traditional GPU memory subsystems that suffer from non-deterministic cache misses and interconnect bus arbitration, deterministic LPU architectures execute memory transfers known at compile-time down to individual clock cycles. 

Result:
1. Zero memory stalls during autoregressive token generation.
2. Constant 500+ tokens/second sustained throughput regardless of batch concurrency.
3. Strict deterministic tail latency (< 1.2ms P99.9).`,
    tokensPerSec: 535,
    ttftMs: 15.5,
    totalTokens: 126,
  },
];

export const BENCHMARK_METRICS: BenchmarkMetric[] = [
  {
    title: "Input To Motion Latency",
    category: "PHYSICS",
    exampleValue: "0.4",
    industryAvg: "16.8",
    unit: "ms",
    percentageLead: 97,
    highlight: "Direct compositor dispatch bypassing main thread queues.",
  },
  {
    title: "Sustained Animation FPS",
    category: "PERFORMANCE",
    exampleValue: "120",
    industryAvg: "54",
    unit: "fps",
    percentageLead: 122,
    highlight: "Hardware-accelerated transforms with zero layout reflows.",
  },
  {
    title: "Interactive Token Stream",
    category: "INFERENCE",
    exampleValue: "542",
    industryAvg: "38",
    unit: "tok/s",
    percentageLead: 1320,
    highlight: "Blinding throughput mirroring next-generation LPUs.",
  },
  {
    title: "Total Memory Footprint",
    category: "OPTIMIZATION",
    exampleValue: "18.4",
    industryAvg: "94.2",
    unit: "MB",
    percentageLead: 80,
    highlight: "Lean asset footprint and strict GPU resource cleanup.",
  },
];

export const TESTIMONIAL_VOICES: TestimonialVoice[] = [
  {
    id: "voice-1",
    quote: "This is what happens when you treat the browser not as a document viewer, but as a high-precision hardware instrument. The kinetic feel is staggering.",
    author: "Elena Rostova",
    role: "Lead Systems Architect",
    org: "Synthetix Labs",
    location: "Zurich",
    verifiedStamp: "VERIFIED PROTOCOL ENGINEER",
  },
  {
    id: "voice-2",
    quote: "The combination of Swiss editorial typography with blinding-speed WebGL telemetry is breathtaking. It sets an impossibly high standard for bespoke client engineering.",
    author: "Marc De Vries",
    role: "Founding Partner",
    org: "Vanguard Studio",
    location: "Amsterdam",
    verifiedStamp: "AWWWARDS JURY MEMBER",
  },
  {
    id: "voice-3",
    quote: "Zero frame drops even with 12,000 WebGL particles and pinned horizontal scroll running simultaneously on an M2 MacBook Air. Absolute masterclass in performance.",
    author: "Kenji Sato",
    role: "Creative Technologist",
    org: "Hyperion Interactive",
    location: "Tokyo",
    verifiedStamp: "GRAPHICS PIPELINE SPECIALIST",
  },
];
